import { Draggable } from '@mint-lib/icons';
import { Button, Card, Checkbox, TruncateText } from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

import type {
  FileSystemNode,
  FolderNode,
} from '../../../../types/fileSystem.js';
import {
  dropZoneClasses,
  useFolderDropZone,
  useNodeDragSource,
} from '../dnd/dndContext.js';
import { FileIcon } from '../FileIcon/FileIcon.jsx';
import { FolderActionsCell } from './FolderActionsCell.jsx';

type Props = {
  folder: FolderNode;
  isSelected: boolean;
  onToggleSelect: (id: string) => void;
  onOpen: (folder: FolderNode) => void;
  onRename: (id: string) => void;
  onDelete: (folder: FolderNode) => void;
  onMove: (nodes: FileSystemNode[]) => void;
};

type NavigationFolderItemProps = {
  folder: Props['folder'];
  onOpen?: Props['onOpen'];
};

const NavigationFolderItem = ({
  folder,
  onOpen,
}: NavigationFolderItemProps) => {
  return (
    <Card
      variant="outlined"
      className="w-full"
      contentClassName="flex flex-row gap-2 items-center justify-start p-2"
      data-dd-action-name="Knowledge base: open navigation folder"
      onDoubleClick={() => onOpen?.(folder)}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          onOpen?.(folder);
        }
      }}
    >
      <FileIcon type={folder.fileType} />
      <TruncateText>{folder.name}</TruncateText>
    </Card>
  );
};

const FolderItem = ({
  folder,
  isSelected,
  onToggleSelect,
  onOpen,
  onRename,
  onDelete,
  onMove,
}: Props) => {
  const { setDropRef, isDropAllowed, isDropRejected } = useFolderDropZone(
    folder.id,
  );
  const { setDragRef, dragHandleProps, isDragging } = useNodeDragSource(folder);

  return (
    <div ref={setDropRef}>
      <Card
        ref={setDragRef}
        variant="outlined"
        className={cn(
          'w-full',
          isDragging && 'opacity-40',
          dropZoneClasses({ isDropAllowed, isDropRejected }),
        )}
        contentClassName="flex flex-row gap-2 items-center justify-start p-2"
        data-dd-action-name="Knowledge base: open folder"
        onDoubleClick={() => onOpen(folder)}
      >
        <Draggable
          {...dragHandleProps}
          data-dd-action-name="Knowledge base: drag folder"
          onDoubleClick={(e) => e.stopPropagation()}
          className="shrink-0 cursor-grab fill-icon-secondary active:cursor-grabbing"
        />

        <Checkbox
          checked={isSelected}
          aria-label={`select-folder-${folder.name}`}
          onCheckedChange={() => onToggleSelect(folder.id)}
          onDoubleClick={(e) => e.stopPropagation()}
        />
        <FileIcon type={folder.fileType} />
        <Button
          variant="plain"
          size="small"
          className="min-w-0 shrink justify-start text-text-primary hover:text-text-primary"
          data-dd-action-name="Knowledge base: open folder"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onOpen(folder);
            }
          }}
        >
          <TruncateText>{folder.name}</TruncateText>
        </Button>
        <FolderActionsCell
          onRename={() => onRename(folder.id)}
          onDelete={() => onDelete(folder)}
          onMove={() => onMove([folder])}
          className="ml-auto"
        />
      </Card>
    </div>
  );
};

export { FolderItem, NavigationFolderItem };
